import React, { useState, useEffect } from 'react';

// Dynamic asset allocation from your local workspace assets structure
import banner1 from '../assets/banner1.jpg';
import banner2 from '../assets/banner2.jpg';
import banner3 from '../assets/banner3.jpg';
import banner4 from '../assets/banner4.jpg';

const AboutUsContent = () => {
  const slideshowImages = [banner1, banner2, banner3, banner4];
  const [activeSlide, setActiveSlide] = useState(0);

  // Automated slider tracking loop configured for 3.5s transitions
  useEffect(() => {
    const sliderTimer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slideshowImages.length);
    }, 3500);
    return () => clearInterval(sliderTimer);
  }, [slideshowImages.length]);

  return (
    <section style={styles.aboutSection} id="about" className="py-5">
      <div className="container my-lg-4">
        <div className="row g-5 align-items-center">
          
          {/* LEFT COLUMN: Modern Multi-layered Animated Image Slider */}
          <div className="col-12 col-lg-6">
            <div style={styles.sliderWindow} className="position-relative overflow-hidden w-100">
              
              {slideshowImages.map((imgSrc, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.slideLayer,
                    backgroundImage: `url(${imgSrc})`,
                    opacity: index === activeSlide ? 1 : 0,
                    transform: index === activeSlide ? 'scale(1)' : 'scale(1.05)',
                  }}
                  className="position-absolute w-100 h-100"
                />
              ))}

              {/* High-End Glassmorphic Visual Masking */}
              <div style={styles.sliderVignette}></div>

              {/* Floating Architectural Badge */}
              <div style={styles.floatingGlassBadge} className="position-absolute bottom-0 start-0 m-4 p-3">
                <span style={styles.badgeLabel} className="d-block text-uppercase">Est. Arusha Registry</span>
                <span style={styles.badgeValue} className="text-white fw-medium">Tanzavibe Holdings Co. Ltd</span>
              </div>

              {/* Slider Dots */}
              <div style={styles.dotCluster} className="position-absolute bottom-0 end-0 m-4 d-flex gap-2">
                {slideshowImages.map((_, idx) => (
                  <span
                    key={idx}
                    style={{
                      ...styles.dotIndicator,
                      backgroundColor: idx === activeSlide ? '#ED8936' : 'rgba(255, 255, 255, 0.25)',
                      width: idx === activeSlide ? '24px' : '6px',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Premium Narrative Content */}
          <div className="col-12 col-lg-6">
            <div className="ps-lg-3">
              <div style={styles.sectionLabel} className="text-uppercase mb-2">Corporate Profile</div>
              <h2 style={styles.sectionTitle} className="fw-medium text-white mb-4">About Us</h2>
              
              <p style={styles.leadParagraph} className="mb-4">
                At Tanzavibe Holdings Company Limited, we believe that sustainable development begins with 
                access to essential resources and the responsible utilization of local potential.
              </p>

              <div style={styles.bodyStack} className="d-flex flex-column gap-3 mb-4">
                <p style={styles.bodyText} className="m-0">
                  Founded in Arusha, Tanzania, our company was established with a commitment to addressing 
                  two critical needs: reducing agricultural waste through value-added food processing and 
                  improving access to clean water through professional drilling and water infrastructure services.
                </p>
                <p style={styles.bodyText} className="m-0">
                  Through our fruit and vegetable dehydration operations, we help preserve the natural goodness 
                  of agricultural produce while extending shelf life, increasing market opportunities for 
                  farmers, and supporting food security. Through our water well drilling services, we help 
                  communities, institutions, and businesses access dependable water sources that promote health, 
                  productivity, and growth.
                </p>
                <p style={styles.bodyText} className="m-0">
                  Our approach combines modern technology, environmental stewardship, and strong partnerships 
                  with local communities. We are passionate about creating solutions that are economically 
                  viable, socially impactful, and environmentally responsible.
                </p>
              </div>

              {/* Integrated Core Statement Frame Block */}
              <div style={styles.quoteBlock} className="p-4 border-start border-3">
                <p style={styles.quoteText} className="m-0 text-white italic">
                  "Transforming resources into opportunities and opportunities into lasting value."
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

const styles = {
  aboutSection: {
    backgroundColor: '#111A2E', // Cool Slate Corporate Base Gray
    fontFamily: "'Libre Franklin', sans-serif",
  },
  sectionLabel: {
    fontSize: '12px',
    color: '#ED8936', // Orange Accent
    letterSpacing: '0.15em',
    fontWeight: '600',
  },
  sectionTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 'calc(2.2rem + 1.2vw)',
  },
  leadParagraph: {
    fontSize: '18px',
    color: '#E2E8F0',
    lineHeight: '1.7',
    fontWeight: '400',
  },
  bodyStack: {
    color: '#CBD5E0',
  },
  bodyText: {
    fontSize: '15px',
    lineHeight: '1.75',
    fontWeight: '300',
  },
  sliderWindow: {
    height: '460px',
    borderRadius: '8px',
    backgroundColor: '#0A0F18',
    border: '1px solid rgba(30, 45, 74, 0.7)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
  },
  slideLayer: {
    top: 0,
    left: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    transition: 'opacity 1s ease-in-out, transform 1.2s ease-in-out',
  },
  sliderVignette: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(17, 26, 46, 0.2) 0%, rgba(10, 15, 24, 0.75) 100%)',
    zIndex: 1,
  },
  floatingGlassBadge: {
    zIndex: 2,
    backgroundColor: 'rgba(10, 15, 24, 0.7)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1px solid rgba(237, 137, 54, 0.3)',
    borderRadius: '4px',
    maxWidth: '85%',
  },
  badgeLabel: {
    fontSize: '10px',
    color: '#718096',
    letterSpacing: '0.12em',
    marginBottom: '2px',
  },
  badgeValue: {
    fontSize: '14px',
    letterSpacing: '0.02em',
  },
  dotCluster: {
    zIndex: 2,
  },
  dotIndicator: {
    height: '6px',
    borderRadius: '3px',
    display: 'inline-block',
    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  quoteBlock: {
    borderColor: '#38A169', // Understated green accent rule
    backgroundColor: 'rgba(56, 161, 105, 0.04)',
    borderRadius: '0 4px 4px 0',
  },
  quoteText: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '22px',
    lineHeight: '1.4',
    fontStyle: 'italic',
  },
};

export default AboutUsContent;